import { makeAutoObservable, runInAction } from 'mobx';

import {
  createAiOpsPreset,
  createAiOpsToken,
  deleteAiOpsPreset,
  deleteAiOpsToken,
  getAiOpsPresetBindings,
  getAiOpsPresets,
  getAiOpsTokens,
  getAppEnvironments,
  saveAiOpsPresetBindings,
  updateAiOpsPreset,
  updateAiOpsToken,
  updateAiOpsTokenEnabled
} from '@/api/aiOpsSetting';

const normalizeAppEnvMap = (options = []) =>
  options.reduce((map, item) => {
    map[item.appEnvId] = {
      appEnvId: item.appEnvId,
      appName: item.appName,
      environment: item.cluster,
      cluster: item.cluster,
      namespace: item.namespace
    };
    return map;
  }, {});

class AiOpsSetting {
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  activeTab = 'token';
  tokenList = [];
  tokenLoading = false;
  presetList = [];
  presetLoading = false;
  appEnvOptions = [];
  appEnvMap = {};
  appEnvLoading = false;
  appEnvRequestId = 0;
  bindingList = [];
  bindingLoading = false;
  bindingKeyword = '';

  setActiveTab(activeTab) {
    this.activeTab = activeTab;
  }

  setBindingKeyword(bindingKeyword) {
    this.bindingKeyword = bindingKeyword;
  }

  clearAppEnvironments() {
    this.appEnvRequestId += 1;
    this.appEnvOptions = [];
    this.appEnvMap = {};
    this.bindingList = [];
    this.bindingKeyword = '';
  }

  async loadTokens() {
    this.tokenLoading = true;
    try {
      const res = await getAiOpsTokens();
      if (res?.code === 200) {
        runInAction(() => {
          this.tokenList = res.data || [];
        });
      }
      return res;
    } finally {
      runInAction(() => {
        this.tokenLoading = false;
      });
    }
  }

  async saveToken(values, editingToken) {
    return editingToken?.id ? updateAiOpsToken(editingToken.id, values) : createAiOpsToken(values);
  }

  async removeToken(tokenId) {
    return deleteAiOpsToken(tokenId);
  }

  async switchTokenEnabled(tokenId, enabled) {
    return updateAiOpsTokenEnabled(tokenId, enabled);
  }

  async loadPresets() {
    this.presetLoading = true;
    try {
      const res = await getAiOpsPresets();
      if (res?.code === 200) {
        runInAction(() => {
          this.presetList = res.data || [];
        });
      }
      return res;
    } finally {
      runInAction(() => {
        this.presetLoading = false;
      });
    }
  }

  async savePreset(values, editingPreset) {
    return editingPreset?.id ? updateAiOpsPreset(editingPreset.id, values) : createAiOpsPreset(values);
  }

  async removePreset(presetId) {
    return deleteAiOpsPreset(presetId);
  }

  async loadAppEnvironments({ force = false } = {}) {
    if (!force && this.appEnvOptions.length > 0) {
      return this.appEnvOptions;
    }
    const requestId = this.appEnvRequestId + 1;
    this.appEnvRequestId = requestId;
    this.appEnvLoading = true;
    try {
      const res = await getAppEnvironments();
      if (res?.code === 200 && requestId === this.appEnvRequestId) {
        runInAction(() => {
          this.appEnvOptions = res.data || [];
          this.appEnvMap = normalizeAppEnvMap(this.appEnvOptions);
        });
      }
      return this.appEnvOptions;
    } catch (error) {
      return this.appEnvOptions;
    } finally {
      runInAction(() => {
        if (requestId === this.appEnvRequestId) {
          this.appEnvLoading = false;
        }
      });
    }
  }

  async loadPresetManagement() {
    return this.loadPresets();
  }

  async loadBindings(presetId) {
    this.bindingLoading = true;
    try {
      const res = await getAiOpsPresetBindings(presetId);
      if (res?.code === 200) {
        runInAction(() => {
          this.bindingList = res.data || [];
        });
      }
      return res;
    } finally {
      runInAction(() => {
        this.bindingLoading = false;
      });
    }
  }

  async saveBindings(presetId, appEnvIds) {
    return saveAiOpsPresetBindings(presetId, appEnvIds);
  }
}

export default new AiOpsSetting();
